import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Clock, Mail, FileText, CheckCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useTranslation } from 'react-i18next';

export default function CheckoutPending() {
  const { t } = useTranslation();
  const location = useLocation();
  const { orderId, profiles, total, email, message } = location.state || {};

  return (
    <div className="min-h-screen pt-24 pb-16 bg-[#F9FAFB] flex items-center justify-center">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-lg px-4"
      >
        <div className="bg-white rounded-3xl shadow-soft p-8 text-center">
          {/* Icon */}
          <div className="w-20 h-20 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <Clock className="w-10 h-10 text-amber-600" />
          </div>
          
          {/* Title */}
          <h1 className="font-heading text-2xl font-bold text-slate-900 mb-2">
            {t('checkout.orderPendingTitle')}
          </h1>
          
          {/* Message */}
          <p className="text-slate-600 mb-6">
            {message || t('checkout.orderPendingMessage')}
          </p>
          
          {/* Order details */}
          {orderId && (
            <div className="bg-slate-50 rounded-xl p-5 mb-6 text-left space-y-4">
              <div className="flex items-center gap-3">
                <FileText className="w-5 h-5 text-slate-400" />
                <div>
                  <p className="text-sm text-slate-500">{t('checkout.orderNumber')}</p>
                  <p className="font-mono font-medium text-slate-900">{orderId}</p>
                </div>
              </div>
              
              {total !== undefined && (
                <div className="flex items-center gap-3">
                  <CheckCircle className="w-5 h-5 text-slate-400" />
                  <div>
                    <p className="text-sm text-slate-500">{t('checkout.totalAmount')}</p>
                    <p className="font-heading text-xl font-bold text-slate-900">${total?.toFixed(2)}</p>
                  </div>
                </div>
              )}
              
              {profiles?.length > 0 && (
                <div>
                  <p className="text-sm text-slate-500 mb-2">{t('checkout.profilesCreated')}</p>
                  <div className="flex flex-wrap gap-2">
                    {profiles.map((p, i) => (
                      <span 
                        key={i} 
                        className="text-xs bg-amber-100 text-amber-700 px-3 py-1 rounded-full font-mono"
                      >
                        {typeof p === 'string' ? p : p.profile_id}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
          
          {/* What happens next */}
          <div className="bg-[#0A2540]/5 border border-[#E5E7EB] rounded-xl p-4 mb-6 text-left">
            <h3 className="font-medium text-blue-900 mb-3 flex items-center gap-2">
              <Mail className="w-4 h-4" />
              {t('checkout.whatHappensNext')}
            </h3>
            <ol className="text-sm text-blue-800 space-y-2">
              <li className="flex items-start gap-2">
                <span className="bg-blue-200 text-blue-800 rounded-full w-5 h-5 flex items-center justify-center text-xs font-medium flex-shrink-0 mt-0.5">1</span>
                <span>{t('checkout.pendingStep1')}</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="bg-blue-200 text-blue-800 rounded-full w-5 h-5 flex items-center justify-center text-xs font-medium flex-shrink-0 mt-0.5">2</span>
                <span>{t('checkout.pendingStep2')}</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="bg-blue-200 text-blue-800 rounded-full w-5 h-5 flex items-center justify-center text-xs font-medium flex-shrink-0 mt-0.5">3</span>
                <span>{t('checkout.pendingStep3')}</span>
              </li>
            </ol>
          </div>
          
          {/* Email notice */}
          {email && (
            <p className="text-sm text-slate-500 mb-6">
              {t('checkout.emailConfirmation')}: <strong>{email}</strong>
            </p>
          )}
          
          {/* Actions */}
          <div className="flex flex-col gap-3">
            <Link to="/">
              <Button className="w-full btn-primary rounded-full" data-testid="back-home-btn">
                {t('common.backToHome')}
              </Button>
            </Link>
            <Link to="/contact">
              <Button variant="outline" className="w-full rounded-full" data-testid="contact-btn">
                {t('checkout.contactUs')}
              </Button>
            </Link>
          </div>
          
          {/* Important notice */}
          <div className="mt-6 pt-6 border-t border-slate-100">
            <p className="text-xs text-slate-400">
              {t('checkout.pendingNotice')}
            </p>
          </div>
        </div>
      </motion.div>
    </div>
  );
}
